import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import { Wallet, CurrencyDollar, ArrowRight } from "@phosphor-icons/react";

const TOOLS = [
  {
    slug: "budget",
    name: "Daily Budget Tracker",
    tagline: "Log daily expenses. See where your money actually goes.",
    description:
      "A zero-friction budget tool. Add expenses, tag by category, and see running totals for the day, week, and month. Data lives in your browser — private by default.",
    icon: Wallet,
    accent: "bg-primary text-primary-foreground",
  },
  {
    slug: "currency",
    name: "Live Currency Rates",
    tagline: "Convert between 160+ world currencies in real time.",
    description:
      "Live foreign-exchange rates sourced from a public FX feed. Convert any amount between USD, EUR, GBP, INR, JPY, and 150+ other currencies.",
    icon: CurrencyDollar,
    accent: "bg-secondary text-secondary-foreground",
  },
];

export default function ToolsIndexPage() {
  return (
    <>
      <SEO
        title="Tools"
        description="Free finance tools from Finance Topia — budget tracker, currency converter, and more."
        url="/tools"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Tools" }]} />
      </div>

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b-2 border-foreground pb-8">
        <div className="overline text-primary">The Toolkit</div>
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight mt-2">
          Practical tools<span className="italic font-normal">.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-foreground/70 leading-relaxed">
          Free, private, no signup required. Built for readers who prefer utilities over
          affiliate links. More tools are added regularly.
        </p>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-0 md:grid-cols-2 border border-foreground/15">
          {TOOLS.map((t, i) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.slug}
                to={`/tools/${t.slug}`}
                data-testid={`tool-card-${t.slug}`}
                className={`p-8 md:p-10 group flex flex-col justify-between min-h-[280px] transition-colors duration-200 hover:bg-foreground hover:text-background ${
                  i === 0 ? "md:border-r border-foreground/15" : ""
                }`}
              >
                <div>
                  <div className={`inline-flex w-14 h-14 border border-foreground/20 items-center justify-center mb-6 ${t.accent}`}>
                    <Icon size={28} weight="bold" />
                  </div>
                  <div className="overline">Tool 0{i + 1}</div>
                  <h2 className="font-display text-3xl font-black mt-2 tracking-tight">
                    {t.name}
                  </h2>
                  <p className="text-sm mt-1 opacity-70 italic">{t.tagline}</p>
                  <p className="text-base mt-4 opacity-80 max-w-md leading-relaxed">
                    {t.description}
                  </p>
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold">
                  Open tool
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </>
  );
}
